import * as React from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { startCase } from 'lodash';
import IndividualTrackerRow from './IndividualTracker';
import { findPierAndTtByTrackerTypeId } from '../../../shared/Helper';
import CloseIcon from '@mui/icons-material/Close';
import { useAtom } from 'jotai';
import { fetchtrackertypeData } from '../../../atoms/setup/SetupAtoms';
import IndividualZimmerBlock from './ZimmermanTracker';

const constructTable = (fields, handleQuantityChange, editableRows, key) => {
  return fields.map((col) => ({
    field: col,
    headerName: startCase(col),
    width: 300,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      if (col === 'distributed_quantities' || col === 'installed_quantities') {
        const row = editableRows.find((r) => r.id === params.row.id);
        // Fix: Get the correct value based on the column type
        const value = col === 'distributed_quantities' 
          ? (row?.distributed_quantities ?? 0)
          : (row?.installed_quantities ?? 0);
        
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="100%"
          >
            <TextField
              type="number"
              value={value}
              variant="outlined"
              size="small"
              onChange={(e) =>
                handleQuantityChange(
                  params.row.id,
                  parseInt(e.target.value || '0', 10),
                  key,
                  col // Pass the column type to distinguish between distributed and installed
                )
              }
            />
          </Box>
        );
      }
      return params.value;
    },
  }));
};


const StepperForm = React.memo(({
  data,
  active,
  tableData,
  selectedRow,
  bomTrackerTypes,
  partCount,
  setPartCount,
  totalCount,
  editableDistributedRows,
  editableInstalledRows,
  setEditableDistributedRows,
  setEditableinstalledRows,
  handleSave,
  svgData,
  pierlegends,
  selectedPier,
  setSelectedPier,
  selectedTt,
  setSelectedTt,
  finalData,
  setCurrentTrackerRow,
  setOpenSecondModal,
  currentProject,
  flattenedTrackerData,
  flattenedNextTrackerData
}) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [, setSvgData] = useAtom(fetchtrackertypeData);
  const [loader, setBtnLoader] = React.useState(false);


  const trackerTypeId = React.useMemo(() => String(data?.tracker_type_id), [data?.tracker_type_id]);

  const getData = React.useCallback((value, id) => {
    const data = value?.bom_per_tracker_type?.find((item) => item?.id === id);
    return data?.distributed_quantity;
  }, []);

  const getRows = React.useCallback((value, selectedRow, section) => {
    if (!value || !selectedRow || !active?.id || !trackerTypeId) return [];

    const allRows = [];
    let temp = finalData?.find(obj => Object.keys(obj).includes(trackerTypeId));
    
    selectedRow.forEach((item, itemIndex) => {
      const trackerId = item?.split('-')?.[2];
      const entries = value?.[section]?.[active?.id] ?? [];

      entries.forEach((entry, entryIndex) => {
        const id = `${section}-${entry.bom_id}-${active?.target?.part_id}-${trackerId}-${itemIndex}-${entryIndex}`;

        allRows.push({
          ...entry,
          quantities_per_tracker: bomTrackerTypes[`${entry?.bom_id}_${trackerId}`],
          distributed_quantities: temp?.[trackerTypeId]
            ? getData(temp[trackerTypeId], id)
            : 0,
          // Add installed_quantities field for installed rows
          installed_quantities: section === 'installed' ? (temp?.[trackerTypeId]
            ? getData(temp[trackerTypeId], id)
            : 0) : 0,
          id,
        });
      });
    });

    return allRows.filter((row) => !!row.quantities_per_tracker);
  }, [active?.id, active?.target?.part_id, bomTrackerTypes, trackerTypeId, finalData, getData]);

  React.useEffect(() => {
    if (selectedRow && selectedRow.length > 0) {
      const trackerRowNumber = Number(selectedRow[0]?.split('-')?.[1]);
      if (!isNaN(trackerRowNumber)) {
        setCurrentTrackerRow(trackerRowNumber);
      }
    }
  }, [selectedRow, setCurrentTrackerRow]);

  const distributedRows = React.useMemo(
    () => getRows(tableData, selectedRow, 'distributed'),
    [getRows, tableData, selectedRow]
  );

  const installedRows = React.useMemo(
    () => getRows(tableData, selectedRow, 'installed'),
    [getRows, tableData, selectedRow]
  );

  const filteredDistributedRows = React.useMemo(
    () => Array.from(
      new Map(distributedRows?.filter(item => item.id.includes(`-${trackerTypeId}-`))?.map(item => [item.bom_id, item])).values()
    ),
    [distributedRows, trackerTypeId]
  );

  const filteredInstalledRows = React.useMemo(
    () => Array.from(
      new Map(installedRows?.filter(item => item.id.includes(`-${trackerTypeId}-`))?.map(item => [item.bom_id, item])).values()
    ),
    [installedRows, trackerTypeId]
  );

  React.useEffect(() => {
    if (!finalData || !trackerTypeId) return;

    let temp = finalData?.find(obj => Object.keys(obj).includes(trackerTypeId));
    if (!temp) return;

    const tempData = findPierAndTtByTrackerTypeId(svgData, data?.tracker_type_id);
    const filter1 = temp?.[trackerTypeId]?.piers?.filter(p => p.status === 1)
      .map(p => p.pier_id);
    const filter2 = temp?.[trackerTypeId]?.torque_tubes?.filter(p => p.status === 1)
      .map(p => p.torque_tube_id);
    const piers = tempData?.pier?.filter(detail => filter1?.includes(detail?.id));
    const tt = tempData?.tt?.filter(detail => filter2?.includes(detail?.id));
    
    setSelectedPier(piers);
    setSelectedTt(tt);

    if (temp?.[trackerTypeId]?.deployed_part_count) {
      setPartCount(temp?.[trackerTypeId]?.deployed_part_count);
    }
  }, [finalData, trackerTypeId, svgData, data?.tracker_type_id, setSelectedPier, setSelectedTt, setPartCount]);

  React.useEffect(() => {
    setEditableDistributedRows(prev => {
      if (filteredDistributedRows.length > 0 && 
          JSON.stringify(prev.map(p => p.id).sort()) !== JSON.stringify(filteredDistributedRows.map(p => p.id).sort())) {
        return filteredDistributedRows;
      }
      return prev;
    });
    
    setEditableinstalledRows(prev => {
      if (filteredInstalledRows.length > 0 && 
          JSON.stringify(prev.map(p => p.id).sort()) !== JSON.stringify(filteredInstalledRows.map(p => p.id).sort())) {
        return filteredInstalledRows;
      }
      return prev;
    });
    // eslint-disable-next-line
  }, [filteredDistributedRows, filteredInstalledRows]);

  const handleQuantityChange = React.useCallback((id, value, key, columnType) => {
    
    if (key === 'installed') {
      setEditableinstalledRows((prev) =>
        prev.map((row) => {
          if (row.id === id) {
            return {
              ...row,
              [columnType === 'installed_quantities' ? 'installed_quantities' : 'distributed_quantities']: value
            };
          }
          return row;
        })
      );
    } else {
      setEditableDistributedRows((prev) =>
        prev.map((row) => {
          if (row.id === id) {
            return {
              ...row,
              [columnType === 'installed_quantities' ? 'installed_quantities' : 'distributed_quantities']: value
            };
          }
          return row;
        })
      );
    }
  }, [setEditableinstalledRows, setEditableDistributedRows]);

  const handleChange = React.useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    setPartCount(isNaN(val) ? '' : val);
  }, [setPartCount]);

  const handleNext = React.useCallback(async () => {
    setBtnLoader(true);
    try {
      await setSvgData(selectedRow?.[0]?.split('-')?.[2]);
    } catch(err) {
      console.error(err);
    } finally {
      setActiveStep((prev) => prev + 1);
      setBtnLoader(false);
    }
  }, [setSvgData, selectedRow]);

  const handleBack = React.useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  const handleClickPier = (value) => {
    setSelectedPier(prev => {
      const exists = prev.some(pier => pier.id === value.id);
      if (exists) {
        return prev.filter(pier => pier.id !== value.id);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleClickTorquetube = (value) => {
    setSelectedTt(prev => {
      const exists = prev.some(pier => pier.id === value.id);
      if (exists) {
        return prev.filter(pier => pier.id !== value.id);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleCloseModal = React.useCallback(() => {
    setOpenSecondModal(false);
  }, [setOpenSecondModal]);

  // Memoize field arrays
  const fields1 = React.useMemo(() => ['part_name', 'description', 'quantities_per_tracker', 'distributed_quantities'], []);
  const fields2 = React.useMemo(() => ['part_name', 'description', 'quantities_per_tracker', 'installed_quantities'], []);

  // Memoize column configurations
  const distributedColumns = React.useMemo(
    () => constructTable(fields1, handleQuantityChange, editableDistributedRows, 'distributed'),
    [fields1, handleQuantityChange, editableDistributedRows]
  );

  const installedColumns = React.useMemo(
    () => constructTable(fields2, handleQuantityChange, editableInstalledRows, 'installed'),
    [fields2, handleQuantityChange, editableInstalledRows]
  );

  const dataGridStyles = React.useMemo(() => ({
    '& .MuiDataGrid-columnHeader': {
      fontWeight: 600,
      backgroundColor: '#f16253',
      color: '#ffffff',
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: 'inherit !important',
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
      outline: 'none',
      backgroundColor: 'inherit',
    },
  }), []);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" fontWeight={600}>
          {data?.tracker_type}
        </Typography>
        <CloseIcon onClick={handleCloseModal} sx={{cursor:"pointer"}} />
      </Box>
      <Typography fontSize={16} sx={{ my: 0.5 }}>
        {active?.label} {'>'} {active?.target?.label}
      </Typography>

      <Stepper activeStep={activeStep} px={8}>
        <Step><StepLabel>Review Parts</StepLabel></Step>
        <Step><StepLabel>Confirm & Save</StepLabel></Step>
      </Stepper>

      {activeStep === 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 3 }}>Distributed Parts</Typography>
          <DataGrid
            rows={editableDistributedRows}
            columns={distributedColumns}
            autoHeight
            hideFooter
            disableColumnMenu
            disableSelectionOnClick
            sx={{ mb: 2, ...dataGridStyles }}
          />

          <Typography variant="h6">Installed Parts</Typography>
          <DataGrid
            rows={editableInstalledRows}
            columns={installedColumns}
            autoHeight
            hideFooter
            disableColumnMenu
            disableSelectionOnClick
            sx={dataGridStyles}
          />

          <Box display="flex" alignItems="center" mt={2} gap={2}>
            <Box>Unit of Count Piers</Box>
            <TextField
              type="number"
              value={partCount ?? ''}
              variant="outlined"
              size="small"
              onChange={handleChange}
              sx={{ width: '150px' }}
            />
            <Box>out of {totalCount}</Box>
          </Box>

          <Box display="flex" justifyContent="flex-end" mt={4}>
            <Button 
              disabled={!partCount || partCount > totalCount} 
              variant="contained" 
              loading={loader} 
              onClick={handleNext}
            >
              Next
            </Button>
          </Box>
        </>
      )}

      {activeStep === 1 && (
        <Box>
          <Box>
            {currentProject?.type === 1 ? 
              <IndividualZimmerBlock
                tableCoordinates={flattenedTrackerData?.table_coordinates}
                data={flattenedTrackerData}
                current={"piers"}
                mapType={1}
                selectedTt={selectedTt}
                selectedPier={selectedPier}
                active={active}
                handleClickPier={handleClickPier}
                handleClickBay={handleClickTorquetube}
              />
              : 
              <IndividualTrackerRow
                pierlegends={pierlegends} 
                data={flattenedNextTrackerData}
                selectedTt={selectedTt}
                selectedPier={selectedPier} 
                type={"qc"}
                handleClickPier={handleClickPier}
                handleClickTorquetube={handleClickTorquetube}
              />
            }
          </Box>
          <Box mt={4} display="flex" justifyContent="space-between">
            <Button variant="outlined" onClick={handleBack}>
              Previous
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
});

StepperForm.displayName = 'StepperForm';

export default StepperForm;